import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { useEditCategoryMutation } from "@modules/bookCategories/services/mutations";
import { BookCategoryFormValues, bookCategorySchema } from "@modules/bookCategories/validations";
import { listBookCategoriesQueryKey } from "@modules/bookCategories/services/queries";

export function useEditBookCategoryModal(modalRef: RefObject<ModalRef>, onComplete?: () => void) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<BookCategoryFormValues>({
    resolver: zodResolver(bookCategorySchema()),
    mode: "onBlur",
  });

  const { mutateAsync, isPending } = useEditCategoryMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listBookCategoriesQueryKey,
        refetchType: 'all',
      });

      open?.({
        type: "success",
        message: translate("book_category_admin.success.edit"),
      });

      onComplete?.();
      modalRef.current?.close();
    },
  });

  const handleSubmit = async (data: BookCategoryFormValues) => {
    const categoryId = modalRef.current?.getId();
    await mutateAsync({ id: categoryId as string, data });
  }

  return {
    methods,
    isPending,
    handleSubmit,
  };
}
